/**
 * MarketingSchedulePage — Phase 32.9.
 *
 * Month calendar view of approved / published campaigns, keyed on
 * executed_at (the date the brain finished and content is ready).
 * Full schedule-store API (drag-to-reschedule, cancel) lands in Phase 32.10
 * once channel publishing is wired; for now this is a read-only calendar.
 */

import { useState, useMemo } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

// ── Types ────────────────────────────────────────────────────────────────────

interface CampaignSummary {
  id: string;
  product_id: string;
  status: string;
  created_at: number;
  executed_at: number | null;
  slug: string | null;
  title_tag: string | null;
}

// ── Data ─────────────────────────────────────────────────────────────────────

function fetchCampaigns(): Promise<{ campaigns: CampaignSummary[]; total: number }> {
  return apiFetch<{ campaigns: CampaignSummary[]; total: number }>('/admin/marketing/campaigns');
}

// ── Calendar helpers ──────────────────────────────────────────────────────────

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function calendarKey(unix: number): string {
  const d = new Date(unix * 1000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function todayKey(): string {
  return calendarKey(Date.now() / 1000);
}

function buildMonthGrid(year: number, month: number): (string | null)[][] {
  // month: 0-indexed
  const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (string | null)[] = Array(firstDay).fill(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(
      `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`,
    );
  }
  // Pad to complete final week
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks: (string | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

// ── Status colors ─────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  approved:  '#6ee7b7',
  published: '#34d399',
};

// ── Sub-components ────────────────────────────────────────────────────────────

function CampaignChip({ c }: { c: CampaignSummary }) {
  const color = STATUS_COLORS[c.status] ?? '#7dd3fc';
  return (
    <Link
      to={`/omega/marketing/campaigns/${c.id}`}
      title={c.title_tag ?? c.product_id}
      style={{
        display: 'block',
        padding: '2px 5px',
        background: `${color}18`,
        border: `1px solid ${color}55`,
        borderRadius: 3,
        fontSize: '7.5pt',
        color,
        fontWeight: 600,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        textDecoration: 'none',
        marginBottom: 2,
        letterSpacing: '0.02em',
      }}
    >
      {c.title_tag ?? c.slug ?? c.product_id}
    </Link>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function MarketingSchedulePage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const now = new Date();
  const [viewYear, setViewYear]   = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());

  const { data, isPending, isError } = useQuery({
    queryKey: ['marketing', 'campaigns'],
    queryFn: fetchCampaigns,
    refetchInterval: 60_000,
  });

  const schedulable = (data?.campaigns ?? []).filter(
    c => (c.status === 'approved' || c.status === 'published') &&
         (c.executed_at !== null || c.created_at),
  );

  // Group by YYYY-MM-DD key using executed_at, falling back to created_at
  const byDay = useMemo(() => {
    const map: Record<string, CampaignSummary[]> = {};
    for (const c of schedulable) {
      const ts = c.executed_at ?? c.created_at;
      const key = calendarKey(ts);
      (map[key] ??= []).push(c);
    }
    return map;
  }, [schedulable]);

  const weeks = useMemo(() => buildMonthGrid(viewYear, viewMonth), [viewYear, viewMonth]);
  const today = todayKey();

  function prevMonth() {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1);
  }

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs
        crumbs={[
          { label: 'OMEGA', to: '/omega' },
          { label: 'Marketing', to: '/omega/marketing' },
          { label: 'Schedule' },
        ]}
      />

      {/* Header + month nav */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '8px 0 16px' }}>
        <h1 style={{ margin: 0, fontSize: '11pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Publish Schedule
        </h1>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={prevMonth}
            style={{ background: 'none', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 4, color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)', fontSize: '10pt', cursor: 'pointer', padding: '2px 10px' }}
          >
            ‹
          </button>
          <span style={{ fontSize: '10pt', fontWeight: 600, minWidth: 130, textAlign: 'center' }}>
            {MONTH_NAMES[viewMonth]} {viewYear}
          </span>
          <button
            onClick={nextMonth}
            style={{ background: 'none', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 4, color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)', fontSize: '10pt', cursor: 'pointer', padding: '2px 10px' }}
          >
            ›
          </button>
          <button
            onClick={() => { setViewYear(now.getFullYear()); setViewMonth(now.getMonth()); }}
            style={{ background: 'none', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 4, color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)', fontSize: '8.5pt', cursor: 'pointer', padding: '3px 8px', letterSpacing: '0.04em' }}
          >
            Today
          </button>
        </div>
      </div>

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError   && <div style={{ color: '#f87171', fontSize: '9pt' }}>Failed to load data.</div>}

      {/* Calendar grid */}
      {!isPending && !isError && (
        <div style={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, overflow: 'hidden' }}>
          {/* Weekday headers */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            {WEEKDAYS.map(d => (
              <div key={d} style={{ padding: '6px 0', textAlign: 'center', fontSize: '8pt', fontWeight: 700, letterSpacing: '0.08em', color: 'var(--omega-ink-subtle)', textTransform: 'uppercase' }}>
                {d}
              </div>
            ))}
          </div>

          {/* Weeks */}
          {weeks.map((week, wi) => (
            <div key={wi} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderTop: wi > 0 ? '1px solid rgba(255,255,255,0.06)' : undefined }}>
              {week.map((day, di) => {
                const isToday = day === today;
                const events = day ? (byDay[day] ?? []) : [];
                return (
                  <div
                    key={di}
                    style={{
                      minHeight: 72,
                      padding: '6px 7px',
                      borderLeft: di > 0 ? '1px solid rgba(255,255,255,0.06)' : undefined,
                      background: isToday ? 'rgba(125,211,252,0.05)' : 'transparent',
                    }}
                  >
                    {day && (
                      <>
                        <div style={{
                          fontSize: '8pt',
                          fontWeight: isToday ? 700 : 400,
                          color: isToday ? '#7dd3fc' : 'var(--omega-ink-subtle)',
                          marginBottom: 4,
                          letterSpacing: isToday ? '0.04em' : undefined,
                        }}>
                          {new Date(day + 'T12:00:00').getDate()}
                        </div>
                        {events.map(c => <CampaignChip key={c.id} c={c} />)}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* Legend */}
      {!isPending && !isError && (
        <div style={{ display: 'flex', gap: 16, marginTop: 14, alignItems: 'center' }}>
          {Object.entries(STATUS_COLORS).map(([s, col]) => (
            <span key={s} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '8.5pt', color: col }}>
              <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: col }} />
              {s.toUpperCase()}
            </span>
          ))}
          <span style={{ marginLeft: 'auto', fontSize: '8pt', color: 'var(--omega-ink-subtle)' }}>
            Dates show when brain completed (executed_at). Channel publish scheduling lands in Phase 32.10.
          </span>
        </div>
      )}

      {/* Empty-state for the whole page when no schedulable campaigns */}
      {!isPending && !isError && schedulable.length === 0 && (
        <div style={{ marginTop: 16, padding: '24px 20px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, textAlign: 'center', color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>
          No approved campaigns yet.{' '}
          <Link to="/omega/marketing/campaigns" style={{ color: '#7dd3fc' }}>
            Review a campaign
          </Link>{' '}
          to see it here.
        </div>
      )}
    </div>
  );
}
